import Dexie, { type EntityTable } from 'dexie'
import type { BasketEntry, CachedPallet, OutboxOp, SettingsRecord } from './types'

/**
 * Durable device state. The backend holds no database, so this is the only place the
 * queued scans and the temporary basket live — both must survive a force-kill.
 */
class PalletScannerDb extends Dexie {
  outbox!: EntityTable<OutboxOp, 'id'>
  basket!: EntityTable<BasketEntry, 'serialNumber'>
  recentPallets!: EntityTable<CachedPallet, 'palletCode'>
  settings!: EntityTable<SettingsRecord, 'key'>

  constructor() {
    super('pallet-scanner')

    this.version(1).stores({
      // [palletCode+seq] is the index the drain loop walks: strict FIFO within a pallet.
      // createdAt is indexed because the home screen's today-counter ranges over it, and
      // Dexie throws on a where() against an unindexed keyPath.
      outbox:
        'id, status, palletCode, seq, nextAttemptAt, createdAt, [palletCode+seq], [status+nextAttemptAt]',
      basket: 'serialNumber, seq',
      recentPallets: 'palletCode, cachedAt',
      settings: 'key',
    })

    this.version(2).stores({
      outbox:
        'id, status, palletCode, seq, nextAttemptAt, createdAt, syncedAt, [palletCode+seq], [status+nextAttemptAt]',
      basket: 'serialNumber, seq',
      recentPallets: 'palletCode, cachedAt',
      settings: 'key',
    })
  }
}

export const db = new PalletScannerDb()

/** Everything an enqueue needs; the rest of the op is bookkeeping this module fills in. */
export type OutboxDraft = Pick<
  OutboxOp,
  | 'kind'
  | 'palletCode'
  | 'serialNumbers'
  | 'overrideExisting'
  | 'captureMethod'
  | 'capturedAt'
  | 'basketSeq'
>

/**
 * The only way to add to the outbox.
 *
 * Allocating seq and inserting the row happen inside one transaction on purpose. Reading
 * max(seq) and then inserting as two steps lets two fast scans both read N and both write
 * N+1, which makes per-pallet FIFO ambiguous — the exact ordering guarantee the drain loop
 * depends on. At 20 scans/min that collision is rare, which is what makes it dangerous.
 *
 * seq derives from max(seq) rather than count() so a drained row can never let a later op
 * reuse a lower number.
 */
export function enqueueOp(draft: OutboxDraft): Promise<OutboxOp> {
  return db.transaction('rw', db.outbox, async () => {
    const last = await db.outbox.orderBy('seq').last()

    const op: OutboxOp = {
      ...draft,
      id: newOperationId(),
      status: 'queued',
      seq: (last?.seq ?? 0) + 1,
      attempts: 0,
      // Eligible on the worker's next pass; backoff only ever pushes this forward.
      nextAttemptAt: 0,
      createdAt: Date.now(),
    }

    await db.outbox.add(op)

    return op
  })
}

/** The basket's counterpart, atomic for the same reason: «لغو آخرین اسکن» reads max(seq). */
export function addBasketEntry(entry: Omit<BasketEntry, 'seq'>): Promise<BasketEntry> {
  return db.transaction('rw', db.basket, async () => {
    const last = await db.basket.orderBy('seq').last()
    const row: BasketEntry = { ...entry, seq: (last?.seq ?? 0) + 1 }

    // put, not add: serialNumber is the key, so a re-scan must not throw here. Whether the
    // operator is warned about the duplicate is the caller's business.
    await db.basket.put(row)

    return row
  })
}

// Not exported: every op id is minted by enqueueOp, so the id and the idempotency key
// cannot drift apart.
function newOperationId(): string {
  // crypto.randomUUID needs a secure context, which the app requires anyway for the
  // camera. The fallback keeps the dev server on plain http usable.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
